import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Settings, Database, Users, Activity, RefreshCw,
  Save, Cpu, BarChart3, Shield, Zap, Bell, Mail, Server, Layers
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ConfigItem({ item, onSave }) {
  const [value, setValue] = useState(item.value);
  const [saving, setSaving] = useState(false);
  const changed = value !== item.value;

  const handleSave = async () => {
    setSaving(true);
    await onSave(item.key, value);
    setSaving(false);
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
      <div className="flex-1 mr-4">
        <p className="text-sm text-white font-medium">{item.label}</p>
        <p className="text-xs text-slate-500 font-mono">{item.key}</p>
      </div>
      <div className="flex items-center gap-2">
        {item.type === 'boolean' ? (
          <button
            onClick={() => setValue(value === 'true' ? 'false' : 'true')}
            className={`w-10 h-6 rounded-full transition-colors relative ${value === 'true' ? 'bg-emerald-500' : 'bg-slate-700'}`}
            data-testid={`config-toggle-${item.key}`}
          >
            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${value === 'true' ? 'left-5' : 'left-1'}`} />
          </button>
        ) : item.type === 'select' ? (
          <select value={value} onChange={(e) => setValue(e.target.value)}
            className="bg-[#11141D] border border-white/10 text-white px-3 py-1.5 font-mono text-sm focus:border-white/25 outline-none"
            data-testid={`config-select-${item.key}`}>
            {(item.options || '').split(',').map(opt => (
              <option key={opt} value={opt.trim()}>{opt.trim()}</option>
            ))}
          </select>
        ) : (
          <input type={item.type === 'number' ? 'number' : 'text'} value={value}
            onChange={(e) => setValue(e.target.value)}
            className="bg-[#11141D] border border-white/10 text-white px-3 py-1.5 font-mono text-sm w-48 focus:border-white/25 outline-none"
            data-testid={`config-input-${item.key}`} />
        )}
        {changed && (
          <button onClick={handleSave} disabled={saving}
            className="p-1.5 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors"
            data-testid={`config-save-${item.key}`}>
            <Save className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [config, setConfig] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [configRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/config`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/stats`, { withCredentials: true })
      ]);
      setConfig(configRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Admin fetch error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveConfig = async (key, value) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/config`, { key, value }, { withCredentials: true });
      setConfig(prev => prev.map(c => c.key === key ? { ...c, value } : c));
    } catch (err) {
      console.error('Save config error:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030508] flex items-center justify-center">
        <Settings className="w-12 h-12 text-slate-500 animate-spin" />
      </div>
    );
  }

  const tradingConfig = config.filter(c => ['initial_capital', 'max_position_size', 'stop_loss_pct', 'take_profit_pct', 'cycle_interval_sec'].includes(c.key));
  const rebalanceConfig = config.filter(c => ['auto_rebalance', 'rebalance_method'].includes(c.key));
  const smtpConfig = config.filter(c => c.key.startsWith('smtp') || c.key === 'alert_email_enabled');

  return (
    <div className="min-h-screen bg-[#030508]" data-testid="admin-page">
      <header className="sticky top-0 z-50 bg-[#030508]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-slate-400 hover:text-white transition-colors" data-testid="admin-back">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-slate-700 flex items-center justify-center">
                  <Settings className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-black tracking-tight text-white" style={{ fontFamily: 'Chivo' }}>
                  Admin <span className="text-slate-400">{t('admin.panel')}</span>
                </span>
              </div>
            </div>
            <button onClick={fetchData} className="text-slate-400 hover:text-white p-2" data-testid="admin-refresh">
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto p-4 md:p-6 space-y-6">
        {/* System Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="admin-stats">
          {[
            { label: 'Usuarios', value: stats?.users || 0, icon: Users, color: 'text-blue-400' },
            { label: 'Agents', value: stats?.agents || 0, icon: Cpu, color: 'text-emerald-400' },
            { label: 'Trades', value: stats?.trades_recorded || 0, icon: Activity, color: 'text-amber-400' },
            { label: 'Cycles', value: stats?.engine_cycles || 0, icon: RefreshCw, color: 'text-cyan-400' },
            { label: 'Snapshots', value: stats?.snapshots || 0, icon: Database, color: 'text-purple-400' },
            { label: 'Alertas', value: stats?.alerts || 0, icon: Bell, color: 'text-rose-400' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-[#090B0F] border border-white/10 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${color}`} />
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</p>
              </div>
              <p className="text-2xl font-black text-white font-mono">{value.toLocaleString()}</p>
            </div>
          ))}
        </div>

        {/* Engine Status */}
        <div className="bg-[#090B0F] border border-white/10 p-4 md:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Server className="w-5 h-5 text-emerald-500" />
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('admin.systemStatus')}</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#11141D] p-3">
              <p className="text-xs text-slate-500 mb-1">{t('admin.engine')}</p>
              <p className={`font-mono text-sm font-bold ${stats?.engine_running ? 'text-emerald-400' : 'text-slate-500'}`}>
                {stats?.engine_running ? 'RUNNING' : 'IDLE'}
              </p>
            </div>
            <div className="bg-[#11141D] p-3">
              <p className="text-xs text-slate-500 mb-1">{t('admin.engineAvailable')}</p>
              <p className={`font-mono text-sm font-bold ${stats?.engine_available ? 'text-emerald-400' : 'text-red-400'}`}>
                {stats?.engine_available ? 'YES' : 'NO'}
              </p>
            </div>
            <div className="bg-[#11141D] p-3">
              <p className="text-xs text-slate-500 mb-1">{t('admin.smtp')}</p>
              <p className={`font-mono text-sm font-bold ${stats?.smtp_configured ? 'text-emerald-400' : 'text-amber-400'}`}>
                {stats?.smtp_configured ? 'CONFIGURED' : 'PENDING'}
              </p>
            </div>
            <div className="bg-[#11141D] p-3">
              <p className="text-xs text-slate-500 mb-1">{t('admin.webSocket')}</p>
              <p className="font-mono text-sm font-bold text-emerald-400">{t('admin.active')}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Trading Configuration */}
          <div className="bg-[#090B0F] border border-white/10 p-4 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-emerald-500" />
              <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('admin.tradingConfig')}</h3>
            </div>
            {tradingConfig.map(item => (
              <ConfigItem key={item.key} item={item} onSave={handleSaveConfig} />
            ))}
          </div>

          {/* Rebalance Configuration */}
          <div className="bg-[#090B0F] border border-white/10 p-4 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Layers className="w-5 h-5 text-amber-500" />
              <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('admin.rebalance')}</h3>
            </div>
            {rebalanceConfig.map(item => (
              <ConfigItem key={item.key} item={item} onSave={handleSaveConfig} />
            ))}
            {stats?.recent_rebalances?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/5">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">{t('admin.recentHistory')}</p>
                {stats.recent_rebalances.slice(0, 3).map((rb, i) => (
                  <div key={i} className="text-xs font-mono text-slate-400 py-1">
                    {rb.method} - {rb.timestamp?.split('T')[0]}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* SMTP / Email Configuration */}
          <div className="bg-[#090B0F] border border-white/10 p-4 md:p-6 lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="w-5 h-5 text-blue-400" />
              <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('admin.emailSmtp')}</h3>
              {!stats?.smtp_configured && (
                <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 font-mono ml-2">{t('admin.pending')}</span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              {smtpConfig.map(item => (
                <ConfigItem key={item.key} item={item} onSave={handleSaveConfig} />
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
